import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Modal, Form, Input, Tag, Space, message, Popconfirm, Checkbox, Divider, Tabs, Typography, Select, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SafetyOutlined, SaveOutlined, HolderOutlined, MenuOutlined, UndoOutlined, SwapOutlined, AppstoreOutlined, OrderedListOutlined } from '@ant-design/icons';
import { roleAPI, permissionAPI, menuOrderAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { modalWidth, gray } from '../theme/tokens';
import { useFillHeight } from '../hooks/useViewportFit';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  DragEndEvent, DragStartEvent, DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PermAction {
  action: string;
  description: string;
}

interface PermItem {
  id: number;
  menu_code: string;
  menu_name: string;
  menu_group: string;
  actions: PermAction[];
}

interface RoleItem {
  id: number;
  role_code: string;
  role_name: string;
  description: string;
  is_system: boolean;
  is_active: boolean;
  permission_action_ids: number[];
}

interface PermActionFull {
  id: number;
  permission_id: number;
  action: string;
  menu_code: string;
  menu_name: string;
  menu_group: string;
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  READ: { label: '조회', color: '#4f46e5' },
  WRITE: { label: '생성/수정', color: '#10b981' },
  DELETE: { label: '삭제', color: '#ef4444' },
  EXPORT: { label: '내보내기', color: '#f59e0b' },
};

const RoleManagementPage: React.FC = () => {
  /* 탭 콘텐츠 영역을 뷰포트 바닥까지 채우는 실측 높이 — 문서 세로스크롤 방지 */
  const { ref: fillRef, height: fillHeight } = useFillHeight(320);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [allPermActions, setAllPermActions] = useState<PermActionFull[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleItem | null>(null);
  const [form] = Form.useForm();
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const { reload: reloadPermissions } = usePermission();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const matrixRes = await permissionAPI.matrix();
      const { permissions: perms, roles: roleList } = matrixRes.data;
      setPermissions(perms);
      setRoles(roleList);

      // Build flat permission_action list with IDs
      const paList: PermActionFull[] = [];
      for (const p of perms) {
        for (const a of p.actions) {
          // We need the permission_action ID. Since the matrix API returns permission_action_ids on roles,
          // we can derive the ID from the backend. Let's use a separate call.
          paList.push({
            id: 0, // placeholder
            permission_id: p.id,
            action: a.action,
            menu_code: p.menu_code,
            menu_name: p.menu_name,
            menu_group: p.menu_group,
          });
        }
      }

      // Get full permission list with action IDs
      const permRes = await permissionAPI.list();
      const fullPaList: PermActionFull[] = [];
      for (const p of permRes.data) {
        for (const a of p.actions) {
          fullPaList.push({
            id: 0, // We need to retrieve actual PA IDs from backend
            permission_id: p.id,
            action: a.action,
            menu_code: p.menu_code,
            menu_name: p.menu_name,
            menu_group: p.menu_group,
          });
        }
      }
      setAllPermActions(fullPaList);

      if (roleList.length > 0 && !selectedRole) {
        setSelectedRole(roleList[0]);
        setCheckedIds(new Set(roleList[0].permission_action_ids));
      }
    } catch {
      message.error('데이터를 불러오지 못했습니다.');
    }
    setLoading(false);
  }, [selectedRole]);

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const selectRole = (role: RoleItem) => {
    setSelectedRole(role);
    setCheckedIds(new Set(role.permission_action_ids));
  };

  const openCreate = () => {
    setEditRole(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (role: RoleItem) => {
    setEditRole(role);
    form.setFieldsValue({
      role_code: role.role_code,
      role_name: role.role_name,
      description: role.description,
    });
    setModalOpen(true);
  };

  const handleSaveRole = async () => {
    try {
      const values = await form.validateFields();
      if (editRole) {
        await roleAPI.update(editRole.id, { role_name: values.role_name, description: values.description });
        message.success('역할이 수정되었습니다.');
      } else {
        await roleAPI.create(values);
        message.success('역할이 생성되었습니다.');
      }
      setModalOpen(false);
      loadData();
    } catch (err: any) {
      if (err?.response?.data?.detail) message.error(err.response.data.detail);
    }
  };

  const handleDeleteRole = async (id: number) => {
    try {
      await roleAPI.delete(id);
      message.success('역할이 삭제되었습니다.');
      if (selectedRole?.id === id) setSelectedRole(null);
      loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '삭제 실패');
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await roleAPI.setPermissions(selectedRole.id, {
        permission_action_ids: Array.from(checkedIds),
      });
      message.success('권한이 저장되었습니다.');
      await reloadPermissions();
      loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '저장 실패');
    }
    setSaving(false);
  };

  // Group permissions by menu_group
  const groupedPerms = permissions.reduce((acc: Record<string, any[]>, p: any) => {
    if (!acc[p.menu_group]) acc[p.menu_group] = [];
    acc[p.menu_group].push(p);
    return acc;
  }, {});

  // Build paId map from permissions data (actions now include id)
  const paIdMap = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const perm of permissions) {
      for (const act of perm.actions) {
        if (act.id) {
          map.set(`${perm.menu_code}:${act.action}`, act.id);
        }
      }
    }
    return map;
  }, [permissions]);

  const togglePa = (menuCode: string, action: string) => {
    const paId = paIdMap.get(`${menuCode}:${action}`);
    if (!paId) return;
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(paId)) next.delete(paId);
      else next.add(paId);
      return next;
    });
  };

  const isChecked = (menuCode: string, action: string) => {
    const paId = paIdMap.get(`${menuCode}:${action}`);
    return paId ? checkedIds.has(paId) : false;
  };

  // Check all actions for a menu
  const toggleMenu = (menuCode: string, actions: PermAction[]) => {
    const allChecked = actions.every(a => isChecked(menuCode, a.action));
    setCheckedIds(prev => {
      const next = new Set(prev);
      actions.forEach(a => {
        const paId = paIdMap.get(`${menuCode}:${a.action}`);
        if (paId) {
          if (allChecked) next.delete(paId);
          else next.add(paId);
        }
      });
      return next;
    });
  };

  // ═══════════════════════════════════════
  // 메뉴 순서 관리 상태
  // ═══════════════════════════════════════
  const [menuOrderGroups, setMenuOrderGroups] = useState<{ group_name: string; group_sort_order: number; menus: { menu_code: string; menu_name: string; sort_order: number }[] }[]>([]);
  const [originalNames, setOriginalNames] = useState<Record<string, string>>({}); // menu_code/group → 원본명
  const [menuOrderLoading, setMenuOrderLoading] = useState(false);
  const [menuOrderSaving, setMenuOrderSaving] = useState(false);

  const loadMenuOrder = async () => {
    setMenuOrderLoading(true);
    try {
      const orderRes = await menuOrderAPI.get();
      setMenuOrderGroups(orderRes.data.groups);
      // 원본명 저장 (그룹은 인덱스 기반)
      const names: Record<string, string> = {};
      orderRes.data.groups.forEach((g: any, i: number) => {
        names[`grp:${i}`] = g.group_name;
        for (const m of g.menus) names[m.menu_code] = m.menu_name;
      });
      setOriginalNames(names);
    } catch { message.error('메뉴 순서를 불러오지 못했습니다.'); }
    setMenuOrderLoading(false);
  };

  // 그룹 드래그 완료
  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = menuOrderGroups.findIndex(g => `group-${g.group_name}` === active.id);
    const newIdx = menuOrderGroups.findIndex(g => `group-${g.group_name}` === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      setMenuOrderGroups(arrayMove(menuOrderGroups, oldIdx, newIdx));
    }
  };

  // 그룹 간 메뉴 이동
  const moveMenuToGroup = (fromGroupIdx: number, menuIdx: number, toGroupIdx: number) => {
    if (fromGroupIdx === toGroupIdx) return;
    const next = [...menuOrderGroups];
    const fromMenus = [...next[fromGroupIdx].menus];
    const [moved] = fromMenus.splice(menuIdx, 1);
    next[fromGroupIdx] = { ...next[fromGroupIdx], menus: fromMenus };
    const toMenus = [...next[toGroupIdx].menus, moved];
    next[toGroupIdx] = { ...next[toGroupIdx], menus: toMenus };
    setMenuOrderGroups(next);
  };

  // 메뉴 드래그 완료 (그룹 내)
  const handleMenuDragEnd = (groupIdx: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const group = menuOrderGroups[groupIdx];
    const oldIdx = group.menus.findIndex(m => m.menu_code === active.id);
    const newIdx = group.menus.findIndex(m => m.menu_code === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      const next = [...menuOrderGroups];
      next[groupIdx] = { ...next[groupIdx], menus: arrayMove(group.menus, oldIdx, newIdx) };
      setMenuOrderGroups(next);
    }
  };

  const renameMenu = (groupIdx: number, menuIdx: number, newName: string) => {
    const next = [...menuOrderGroups];
    const menus = [...next[groupIdx].menus];
    menus[menuIdx] = { ...menus[menuIdx], menu_name: newName };
    next[groupIdx] = { ...next[groupIdx], menus };
    setMenuOrderGroups(next);
  };

  const renameGroup = (groupIdx: number, newName: string) => {
    const next = [...menuOrderGroups];
    next[groupIdx] = { ...next[groupIdx], group_name: newName };
    setMenuOrderGroups(next);
  };

  const handleSaveMenuOrder = async () => {
    setMenuOrderSaving(true);
    try {
      const payload = {
        groups: menuOrderGroups.map((g, gi) => ({
          group_name: g.group_name,
          group_sort_order: gi * 10,
          menus: g.menus.map((m, mi) => ({ menu_code: m.menu_code, sort_order: mi + 1, menu_name: m.menu_name })),
        })),
      };
      await menuOrderAPI.save(payload);
      message.success('메뉴 순서가 저장되었습니다.');
      await reloadPermissions();
    } catch (err: any) { message.error(err?.response?.data?.detail || '저장 실패'); }
    setMenuOrderSaving(false);
  };

  const handleResetMenuOrder = async () => {
    try {
      await menuOrderAPI.reset();
      message.success('기본 순서로 초기화되었습니다.');
      await reloadPermissions();
      loadMenuOrder();
    } catch { message.error('초기화에 실패했습니다.'); }
  };

  // ═══════════════════════════════════════
  // 렌더링
  // ═══════════════════════════════════════
  const permMatrixTab = (
    /* 높이는 부모(실측 fill 컨테이너)가 정한다 — 긴 매트릭스는 카드 본문 안에서 스크롤 */
    <div style={{ display: 'flex', gap: 16, height: '100%' }}>
      {/* Left: Role List */}
      <Card
        title={<span><SafetyOutlined style={{ marginRight: 8 }} />역할 목록</span>}
        extra={<Button size="small" icon={<PlusOutlined />} onClick={openCreate}>추가</Button>}
        style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        styles={{ body: { padding: 0, flex: 1, minHeight: 0, overflow: 'auto' } }}
      >
        <div style={{ padding: 8 }}>
          {roles.map(r => (
            <div
              key={r.id}
              onClick={() => selectRole(r)}
              style={{
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                background: selectedRole?.id === r.id ? 'rgba(79,70,229,0.08)' : 'transparent',
                border: selectedRole?.id === r.id ? '1px solid rgba(79,70,229,0.2)' : '1px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: gray[800] }}>{r.role_name}</div>
                  <div style={{ fontSize: 11, color: gray[400] }}>{r.role_code}</div>
                </div>
                <Space size={4}>
                  {r.is_system && <Tag color="blue" style={{ fontSize: 10 }}>시스템</Tag>}
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEdit(r); }} />
                  {!r.is_system && (
                    <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDeleteRole(r.id)} okText="삭제" cancelText="취소">
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                    </Popconfirm>
                  )}
                </Space>
              </div>
              {r.description && <div style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{r.description}</div>}
            </div>
          ))}
        </div>
      </Card>

      {/* Right: Permission Matrix */}
      <Card
        title={selectedRole ? `"${selectedRole.role_name}" 권한 설정` : '역할을 선택하세요'}
        extra={selectedRole && (
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSavePermissions}>
            권한 저장
          </Button>
        )}
        style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        styles={{ body: { flex: 1, minHeight: 0, overflow: 'auto' } }}
      >
        {selectedRole ? (
          <div>
            {Object.entries(groupedPerms).map(([group, perms]) => (
              <div key={group} style={{ marginBottom: 24 }}>
                <Typography.Text strong style={{ fontSize: 14, color: '#4f46e5' }}>{group}</Typography.Text>
                <Divider style={{ margin: '8px 0' }} />
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, color: gray[500], width: 200 }}>메뉴</th>
                      {['READ', 'WRITE', 'DELETE', 'EXPORT'].map(act => (
                        <th key={act} style={{ padding: '8px 12px', textAlign: 'center', fontSize: 12, color: gray[500], width: 100 }}>
                          <span style={{ color: ACTION_LABELS[act]?.color }}>{ACTION_LABELS[act]?.label}</span>
                        </th>
                      ))}
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 12, color: gray[500], width: 80 }}>전체</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(perms as any[]).map((p: any) => {
                      const availableActions = p.actions.map((a: any) => a.action);
                      const allChecked = availableActions.every((a: string) => isChecked(p.menu_code, a));
                      return (
                        <tr key={p.menu_code} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 500 }}>{p.menu_name}</td>
                          {['READ', 'WRITE', 'DELETE', 'EXPORT'].map(act => (
                            <td key={act} style={{ padding: '8px 12px', textAlign: 'center' }}>
                              {availableActions.includes(act) ? (
                                <Checkbox
                                  checked={isChecked(p.menu_code, act)}
                                  onChange={() => togglePa(p.menu_code, act)}
                                />
                              ) : (
                                <span style={{ color: '#e2e8f0' }}>-</span>
                              )}
                            </td>
                          ))}
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <Checkbox
                              checked={allChecked}
                              indeterminate={!allChecked && availableActions.some((a: string) => isChecked(p.menu_code, a))}
                              onChange={() => toggleMenu(p.menu_code, p.actions)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 60, color: gray[400] }}>
            <SafetyOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
            <div>왼쪽에서 역할을 선택하면 권한을 설정할 수 있습니다.</div>
          </div>
        )}
      </Card>
    </div>
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // ── Sortable 그룹 컴포넌트 ──
  const SortableGroup: React.FC<{ group: typeof menuOrderGroups[0]; gi: number; id: string }> = ({ group, gi, id }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginBottom: 16,
      border: isDragging ? '2px dashed #4f46e5' : '1px solid #e2e8f0',
      borderRadius: 12,
      overflow: 'hidden' as const,
      background: '#fff',
    };
    const origVal = originalNames[`grp:${gi}`] || '';
    const isGroupChanged = origVal !== '' && origVal !== group.group_name;
    return (
      <div ref={setNodeRef} style={style}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 12px', background: isGroupChanged ? '#fef3c7' : '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <Space size={6}>
            <span {...attributes} {...listeners} style={{ cursor: 'grab', display: 'inline-flex', padding: '4px 2px' }}>
              <HolderOutlined style={{ color: gray[400], fontSize: 14 }} />
            </span>
            <Input
              value={group.group_name}
              onChange={(e) => renameGroup(gi, e.target.value)}
              style={{
                fontWeight: 700, fontSize: 14, padding: '2px 8px', width: 220, borderRadius: 6,
                color: isGroupChanged ? '#92400e' : '#4f46e5',
                background: isGroupChanged ? '#fffbeb' : '#fff',
                borderColor: isGroupChanged ? '#fbbf24' : '#e2e8f0',
              }}
            />
            {isGroupChanged && <Tag color="warning" style={{ fontSize: 10, margin: 0, lineHeight: '18px' }}>수정됨</Tag>}
          </Space>
          <span style={{ fontSize: 11, color: '#b0b8c4' }}>{group.menus.length}개 메뉴</span>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMenuDragEnd(gi)}>
          <SortableContext items={group.menus.map(m => m.menu_code)} strategy={verticalListSortingStrategy}>
            {group.menus.map((menu, mi) => (
              <SortableMenu key={menu.menu_code} menu={menu} gi={gi} mi={mi} id={menu.menu_code}
                isLast={mi === group.menus.length - 1} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    );
  };

  // ── Sortable 메뉴 컴포넌트 ──
  const SortableMenu: React.FC<{ menu: typeof menuOrderGroups[0]['menus'][0]; gi: number; mi: number; id: string; isLast: boolean }> = ({ menu, gi, mi, id, isLast }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const origName = originalNames[menu.menu_code] || '';
    const isMenuChanged = origName !== '' && origName !== menu.menu_name;
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '5px 12px 5px 20px',
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
      background: isDragging ? '#eef2ff' : isMenuChanged ? '#fefce8' : 'transparent',
    };
    const otherGroups = menuOrderGroups
      .map((g, i) => ({ idx: i, name: g.group_name }))
      .filter(g => g.idx !== gi);
    return (
      <div ref={setNodeRef} style={style}>
        <Space size={8} style={{ flex: 1, minWidth: 0 }}>
          <span {...attributes} {...listeners} style={{ cursor: 'grab', display: 'inline-flex', padding: '4px 2px' }}>
            <HolderOutlined style={{ color: '#cbd5e1', fontSize: 12 }} />
          </span>
          <Input
            value={menu.menu_name}
            onChange={(e) => renameMenu(gi, mi, e.target.value)}
            style={{
              fontSize: 13, padding: '2px 8px', width: 180, borderRadius: 6,
              color: isMenuChanged ? '#92400e' : '#334155',
              background: isMenuChanged ? '#fffbeb' : '#fff',
              borderColor: isMenuChanged ? '#fbbf24' : '#e2e8f0',
            }}
          />
          {isMenuChanged && (
            <Tag color="warning" style={{ fontSize: 10, margin: 0, lineHeight: '18px' }}>수정됨</Tag>
          )}
        </Space>
        <Tooltip title="다른 그룹으로 이동">
          <Select
            size="small"
            placeholder={<SwapOutlined style={{ fontSize: 11 }} />}
            style={{ width: 110, fontSize: 11 }}
            value={undefined}
            onChange={(toIdx: number) => moveMenuToGroup(gi, mi, toIdx)}
            options={otherGroups.map(g => ({ value: g.idx, label: g.name }))}
            popupMatchSelectWidth={false}
          />
        </Tooltip>
      </div>
    );
  };

  const menuOrderTab = (
    <Card
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      styles={{ body: { flex: 1, minHeight: 0, overflow: 'auto' } }}
      title={<span><MenuOutlined style={{ marginRight: 8 }} />메뉴 순서 관리</span>}
      extra={
        <Space>
          <Popconfirm title="기본 순서로 초기화하시겠습니까?" onConfirm={handleResetMenuOrder} okText="초기화" cancelText="취소">
            <Button icon={<UndoOutlined />}>초기화</Button>
          </Popconfirm>
          <Button type="primary" icon={<SaveOutlined />} loading={menuOrderSaving} onClick={handleSaveMenuOrder}>
            순서 저장
          </Button>
        </Space>
      }
    >
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
        <HolderOutlined /> 핸들을 드래그하여 순서를 변경하고, 오른쪽 셀렉트로 메뉴를 다른 그룹으로 이동할 수 있습니다.
      </Typography.Text>
      <div style={{ maxWidth: 600 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
          <SortableContext items={menuOrderGroups.map(g => `group-${g.group_name}`)} strategy={verticalListSortingStrategy}>
            {menuOrderGroups.map((group, gi) => (
              <SortableGroup key={`group-${group.group_name}`} group={group} gi={gi} id={`group-${group.group_name}`} />
            ))}
          </SortableContext>
        </DndContext>
        {menuOrderGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: gray[400] }}>
            <MenuOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
            <div>메뉴 데이터를 불러오는 중...</div>
          </div>
        )}
      </div>
    </Card>
  );

  const [activeTab, setActiveTab] = useState('permissions');

  return (
    <div>
      {/* 탭 헤더 - 카드형 구분 */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20,
      }}>
        {[
          {
            key: 'permissions',
            icon: <AppstoreOutlined />,
            title: '권한 매트릭스',
            desc: '역할별 메뉴/기능 접근 권한을 관리합니다',
            color: '#4f46e5',
            bg: '#eef2ff',
            borderColor: '#c7d2fe',
          },
          {
            key: 'menu-order',
            icon: <OrderedListOutlined />,
            title: '메뉴 순서',
            desc: '사이드바 메뉴 그룹 및 순서를 변경합니다',
            color: '#0891b2',
            bg: '#ecfeff',
            borderColor: '#a5f3fc',
          },
        ].map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <div
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key === 'menu-order') loadMenuOrder();
              }}
              style={{
                flex: 1, maxWidth: 340, padding: '16px 20px',
                borderRadius: 12, cursor: 'pointer',
                background: isActive ? tab.bg : '#fff',
                border: `2px solid ${isActive ? tab.color : '#e2e8f0'}`,
                boxShadow: isActive ? `0 2px 12px ${tab.color}18` : '0 1px 3px rgba(0,0,0,0.04)',
                transition: 'all 0.25s ease',
                position: 'relative' as const,
                overflow: 'hidden' as const,
              }}
            >
              {isActive && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg, ${tab.color}, ${tab.borderColor})`,
                }} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: isActive ? tab.color : '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, color: isActive ? '#fff' : '#94a3b8',
                  transition: 'all 0.25s ease',
                  boxShadow: isActive ? `0 2px 8px ${tab.color}40` : 'none',
                }}>
                  {tab.icon}
                </div>
                <div>
                  <div style={{
                    fontSize: 14, fontWeight: 700,
                    color: isActive ? tab.color : '#475569',
                  }}>
                    {tab.title}
                  </div>
                  <div style={{ fontSize: 11, color: isActive ? tab.color + 'aa' : '#94a3b8', marginTop: 2 }}>
                    {tab.desc}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 탭 콘텐츠 — 뷰포트 바닥까지 실측한 높이 안에서 카드 본문만 스크롤 (문서 세로스크롤 방지) */}
      <div ref={fillRef} style={{ height: fillHeight }}>
        <div style={{ display: activeTab === 'permissions' ? 'block' : 'none', height: '100%' }}>
          {permMatrixTab}
        </div>
        <div style={{ display: activeTab === 'menu-order' ? 'block' : 'none', height: '100%' }}>
          {menuOrderTab}
        </div>
      </div>

      {/* Role Create/Edit Modal */}
      <Modal
        title={editRole ? '역할 수정' : '역할 추가'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSaveRole}
        okText={editRole ? '수정' : '생성'}
        width={modalWidth.sm}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="role_code" label="역할 코드" rules={[{ required: !editRole, message: '역할 코드를 입력하세요' }]}>
            <Input disabled={!!editRole} placeholder="MANAGER" style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item name="role_name" label="역할 이름" rules={[{ required: true, message: '역할 이름을 입력하세요' }]}>
            <Input placeholder="관리 담당자" />
          </Form.Item>
          <Form.Item name="description" label="설명">
            <Input.TextArea rows={2} placeholder="역할 설명" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagementPage;
